import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ identifier: "", password: "" });
  const updateIdentifier = (value) => {
    const identifier = value.includes("@") ? value : value.toUpperCase();
    setForm((current) => ({ ...current, identifier }));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "session_expired") {
      toast.warning("Your session has expired. Please sign in again.");
      // Clean the query param so a page refresh doesn't re-show the toast.
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const submit = async () => {
    if (!form.identifier || !form.password) {
      toast.error("Please enter your Emp Code or email and password.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/login", { email: form.identifier, password: form.password });
      setUser(res.data);
      navigate("/dashboard", { replace: true, state: { user: res.data } });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md -mt-16">
          <div className="flex flex-col items-center mb-8">
            <a
              href="https://globalhealthcareresource.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Global Healthcare Resource website (opens in a new tab)"
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            >
              <img src="/ghr-full-logo.jpg" alt="Global Healthcare Resource" className="h-24 w-auto mx-auto mb-7 dark:rounded-xl" data-testid="login-global-logo" />
            </a>
            <div data-testid="login-app-brand" className="max-w-full text-center text-xl md:text-2xl font-bold leading-tight text-foreground" style={{ fontFamily: '"DM Sans", sans-serif' }}>
              Global CRT (Client Revenue Tracker)
            </div>
          </div>

          <div className="rounded-2xl border bg-card shadow-[var(--app-shadow)] p-6 md:p-8">
            <h2 className="text-lg font-semibold text-foreground">Sign in to your workspace</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">
              Manage revenue, FTEs, and client engagements in one place.
            </p>

            <form
              data-testid="auth-login-form"
              autoComplete="off"
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="identifier">Emp Code or Email</Label>
                <Input id="identifier" name="login-identifier" type="text" autoComplete="off" autoCapitalize="none"
                  autoCorrect="off" spellCheck={false} data-testid="auth-identifier-input"
                  placeholder="Ex: GA00000 or user@globalhealthcareresource.com"
                  value={form.identifier} onChange={(e) => updateIdentifier(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" name="login-password" type="password" autoComplete="new-password"
                  autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  data-testid="auth-password-input" placeholder="Enter your password"
                  value={form.password} onChange={(e) => setForm((current) => ({ ...current, password: e.target.value }))} />
              </div>
              <Button type="submit" data-testid="auth-submit-button" disabled={submitting}
                className="w-full h-10 rounded-xl mt-1">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 flex items-start gap-2 rounded-lg bg-muted/50 border p-3">
              <Lock className="h-3.5 w-3.5 text-muted-foreground/70 mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground leading-relaxed" data-testid="restricted-access-notice">
                Access is restricted to authorized Global Healthcare Resource team members only.
                Accounts are created by administrators — contact your admin if you need access.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
